import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  ShoppingCart,
  Trash2,
  Calendar,
  Clock,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  useCartOpenState,
  useCartQuery,
  useRemoveLineItemsFromCartMutation,
} from '../../hooks/useCart';
import { CartLine } from '../../types/cart';

// ─── Helpers ───

function formatTime(timeStr: string | null | undefined): string {
  if (!timeStr) return '';
  const parts = timeStr.toString().split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parts[1] || '00';
  const ampm = hours >= 12 ? 'pm' : 'am';
  const h = hours % 12 || 12;
  return `${h}:${minutes}${ampm}`;
}

function formatPrice(subUnits: number, currency: string = 'AUD'): string {
  if (!subUnits || subUnits === 0) return 'Free';
  return `$${(subUnits / 100).toFixed(2)}`;
}

const dayCodeToShort: Record<string, string> = {
  MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun',
};

// ─── Cart Line Item ───

const CartLineItem = ({
  line,
  onRemove,
  isRemoving,
}: {
  line: CartLine;
  onRemove: () => void;
  isRemoving: boolean;
}) => {
  const intl = useIntl();

  return (
    <div className="flex gap-3 py-3">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="font-medium text-sm truncate">{line.activityTitle}</div>
        <div className="text-xs text-muted-foreground truncate">
          {line.sessionLabel}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {line.daysOfWeek && line.daysOfWeek.length > 0 && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {line.daysOfWeek.map(d => dayCodeToShort[d] || d).join(', ')}
            </span>
          )}
          {line.startTime && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTime(line.startTime)} - {formatTime(line.endTime)}
            </span>
          )}
        </div>
        {line.childName && (
          <Badge variant="secondary" className="text-xs">
            {line.childName}
          </Badge>
        )}
      </div>
      <div className="flex flex-col items-end justify-between">
        <span className="font-semibold text-sm">
          {formatPrice(line.priceSubUnits, line.currency)}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          disabled={isRemoving}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// ─── Cart Drawer ───

const CartDrawer = () => {
  const intl = useIntl();
  const history = useHistory();
  const { isOpen, close } = useCartOpenState();
  const { data: cart, isLoading } = useCartQuery();
  const removeLines = useRemoveLineItemsFromCartMutation();

  const lines = cart?.lines || [];
  const totalAmount = cart?.totalAmount || 0;
  const currency = cart?.currency || 'AUD';
  const lineCount = lines.length;

  const handleRemoveLine = (lineId: string) => {
    removeLines.mutate([lineId]);
  };

  const handleCheckout = () => {
    close();
    // Navigate to checkout — will be wired to Sharetribe transaction flow
    history.push('/checkout');
  };

  return (
    <Sheet open={!!isOpen} onOpenChange={open => !open && close()}>
      <SheetContent side="right" className="flex flex-col w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            {intl.formatMessage({ id: 'CartDrawer.title' })}
            {lineCount > 0 && (
              <Badge variant="secondary" className="ml-auto">
                {lineCount}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {lineCount === 0
              ? intl.formatMessage({ id: 'CartDrawer.empty' })
              : intl.formatMessage(
                  { id: 'CartDrawer.itemCount' },
                  { count: lineCount }
                )}
          </SheetDescription>
        </SheetHeader>

        {/* Line items */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {intl.formatMessage({ id: 'CartDrawer.loading' })}
            </div>
          ) : lineCount === 0 ? (
            <div className="py-12 text-center space-y-3">
              <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                {intl.formatMessage({ id: 'CartDrawer.emptyMessage' })}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  close();
                  history.push('/s');
                }}
              >
                {intl.formatMessage({ id: 'CartDrawer.browseActivities' })}
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {lines.map((line: CartLine) => (
                <CartLineItem
                  key={line.id}
                  line={line}
                  onRemove={() => handleRemoveLine(line.id)}
                  isRemoving={removeLines.isLoading}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer with total + checkout */}
        {lineCount > 0 && (
          <div className="pt-4 space-y-4">
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {intl.formatMessage({ id: 'CartDrawer.total' })}
              </span>
              <span className="text-lg font-bold">
                {formatPrice(totalAmount, currency)}
              </span>
            </div>
            <Button className="w-full" size="lg" onClick={handleCheckout}>
              {intl.formatMessage({ id: 'CartDrawer.checkout' })}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
